import { select, Store } from '@ngrx/store';
import { State } from '@app/store/state/state';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Dictionary } from '@ngrx/entity';

import { 
  fetchPrimeModalEnable, setAppliedDateFilterTypeAction, sortingHasBeenChangedAction } from '@app/store/actions/global.actions';
import {
  selectGlobalUiFiltersByResultsHandle, selectGlobalUiResultsHandle
} from '@app/store/selectors/global-ui-results.selector';
import { selectErrorHandle } from '@app/store/selectors/error-handle.selector';
import { ErrorHandle } from '@shared/no-results/entities/results-errors';
import { globalSelector } from '@app/store/selectors/global.selector';
import { UserBenefitResponse } from '@shared/benefits/entities/benefits';
import {
  AirFilterResponseVm, AirFilterVm, HotelFiltersResponse, HotelFilterVm
} from '@shared/magic-filter-bar/entities/filter-types';
import { AirFiltersVmPipe } from '@app/pipes/global-filters/air-filters-vm.pipe';
import { BusCompany } from '@app/entities/bus-company';
import { mapHotelFiltersVm } from '@app/mappers/global-filters/hotel-global-filters.mapper';
import { NavigationItem } from '@app/entities/router-state.entity';
import { DateWidgetType } from '@shared/magic-filter-bar/date-filter/entities/date-filter';
import { selectStaticLists } from '@app/store/selectors/static-lists.selector';
import { ProcessType, StaticList } from '@app/modules/core/interfaces/static-lists';
import { ReservationType } from '@user-history/entities/user-history-item';

@Injectable()
export class GlobalFacade {

  constructor(
    private readonly store: Store<State>,
    private readonly airFiltersVmPipe: AirFiltersVmPipe,
  ) { }

  public benefits$: Observable<UserBenefitResponse[]> = this.store.pipe(
    select(globalSelector),
    map(state => state?.benefits)
  );

  public airFilters$: Observable<AirFilterResponseVm> = this.store.pipe(
    select(globalSelector),
    map(state => state?.airFilters)
  );

  public hotelFilters$: Observable<HotelFiltersResponse> = this.store.pipe(
    select(globalSelector),
    map(state => state?.hotelFilters)
  );

  public modalPrimeEnable$: Observable<boolean> = this.store.pipe(
    select(globalSelector),
    map(state => state?.modalPrime)
  );

  public staticLists$: Observable<StaticList[]> = this.store.pipe(select(selectStaticLists));

  public airFilterVm$(
    busCompanies: Dictionary<BusCompany> | undefined = undefined,
    isBuses: boolean | undefined = false
  ): Observable<AirFilterVm[]> {
    return this.airFilters$.pipe(
      map(airFilters => this.airFiltersVmPipe.transform(airFilters, busCompanies, isBuses))
    );
  }

  public hotelFilterVm$(): Observable<HotelFilterVm[]> {
    return this.hotelFilters$.pipe(
      map(hotelFilters => mapHotelFiltersVm(hotelFilters))
    );
  }

  public fetchPrimeModal(isEnable: boolean): void {
    this.store.dispatch(fetchPrimeModalEnable({ isEnable }));
  }

  public getStaticList(processType: ProcessType, productType: ReservationType): Observable<StaticList | null> {
    return this.staticLists$.pipe(
      map((lists) => {
        return lists.find(list => list.process_type === processType && list.product_type === productType) || null
      })
    );
  }  

  private readonly errorHandle$: Observable<ErrorHandle> = this.store.pipe(select(selectErrorHandle));

  private readonly navigation$: Observable<NavigationItem[]> = this.store.pipe(
    map((state) => state?.globalUi?.navigation)
  );

  private readonly sortingHasBeenChanged$: Observable<boolean> = this.store.pipe(
    select(selectGlobalUiFiltersByResultsHandle),
    map(state => state?.sortingHasBeenChanged)
  );

  private readonly appliedDateFilterType$: Observable<DateWidgetType> = this.store.pipe(
    select(selectGlobalUiFiltersByResultsHandle),
    map(state => state?.appliedDateFilterType)
  );

  private readonly isLoadingResults$: Observable<boolean> = this.store.pipe(
    select(selectGlobalUiResultsHandle),
    map(state => state?.isLoadingResults)
  );

  private readonly isLoadingResultsDetail$: Observable<boolean> = this.store.pipe(
    select(selectGlobalUiResultsHandle),
    map(state => state?.isLoadingResultsDetail)
  );

  private sortingHasBeenChanged(payload: boolean): void {
    this.store.dispatch(sortingHasBeenChangedAction({ payload }));
  }

  private setAppliedDateFilterType(payload: DateWidgetType): void {
    this.store.dispatch(setAppliedDateFilterTypeAction({ payload }));
  }

  private readonly filters = {
    sortingHasBeenChanged$: this.sortingHasBeenChanged$,
    appliedDateFilterType$: this.appliedDateFilterType$,
    sortingHasBeenChanged: this.sortingHasBeenChanged.bind(this),
    setAppliedDateFilterType: this.setAppliedDateFilterType.bind(this),
  }

  private readonly results = {
    filters: this.filters,
    isLoadingResults$: this.isLoadingResults$,
    isLoadingResultsDetail$: this.isLoadingResultsDetail$,
  }

  public ui = {
    errorHandle$: this.errorHandle$,
    navigation$: this.navigation$,
    results: this.results
  }
}
